import { AppCtx, useApp, View, viewAllowed } from '../App';
import { useChat } from '../chat/ChatProvider';
import { Avatar } from '../components/Avatar';
import { mobileModules, Tab, tabsOf } from '../nav/mobile';
import { IconMore } from '../components/icons';
import { sectionOf } from '../nav/sections';

/*
 * Нижняя полоска разделов — только на телефоне (CSS). Видна на любой
 * глубине стопки: тап по другому разделу ведёт в него, тап по текущему
 * сворачивает всё до корня. Формат как в мессенджерах: иконка и подпись.
 *
 * Состав полосы не постоянный: он выводится из мобильной раскладки
 * (nav/mobile.ts). Мест пять, последнее у профиля; разделы, которым места не
 * хватило, уходят во вкладку «Ещё».
 */

/** Состав полосы: раскладка телефона даёт разделы, назначение решает, есть ли право. */
export function mobileTabs(app: AppCtx) {
  const modules = mobileModules(app.mobile).filter((m) => viewAllowed(app.hasAccess, m as View, true));
  return tabsOf(modules);
}

export function TabBar() {
  const app = useApp();
  const chat = useChat();
  const current = app.view;
  const name = `${app.user.firstName} ${app.user.lastName}`.trim() || app.user.login;

  const tap = (v: View) => {
    // Уже в корне раздела — свернуть его стопку; в подстранице раздела
    // (оргструктура, проект) — вернуться в корень; иначе перейти.
    if (app.view === v) app.resetSection();
    else app.showView(v);
  };

  const { tabs } = mobileTabs(app);

  const titleOf = (t: Tab): string =>
    t === 'more' ? 'Ещё' : t === 'profile' ? 'Профиль' : (sectionOf(t)?.title ?? t);
  const iconOf = (t: Tab) => {
    if (t === 'more') return <IconMore />;
    if (t === 'profile') return <Avatar login={app.user.login} name={name} size={24} />;
    const s = sectionOf(t);
    return s ? <s.Icon /> : null;
  };

  return (
    <nav className="tabbar" aria-label="Разделы">
      {tabs.map((t) => (
        <button
          key={t}
          className={`tab${current === t ? ' on' : ''}`}
          onClick={() => tap(t as View)}
          aria-current={current === t ? 'page' : undefined}
        >
          {iconOf(t)}
          {t === 'chat' && chat.unreadTotal ? (
            <span className="tab-badge">{chat.unreadTotal > 99 ? '99+' : chat.unreadTotal}</span>
          ) : null}
          <span>{titleOf(t)}</span>
        </button>
      ))}
    </nav>
  );
}
